import axios from "axios";
import i18next from "i18next";
import { developmentMode } from "../constants";
import MockApiAdapter from "./mockApiAdapter";

console.log("Version: " + process.env.REACT_APP_VERSION);
console.log("Using server: " + process.env.REACT_APP_WEB_SERVICE_URL);

const logAPI = (str) => {
    const css = "font-size: 20px; color: #003585";
    console.log(`%c${str}`, css);
};

//======================================================================================================================
// Session
//======================================================================================================================

const createSession = (username, token) => {
    localStorage.setItem("session", JSON.stringify({ username, token }));
};

const getSession = () => {
    const data = localStorage.getItem("session");
    const session = data ? JSON.parse(data) : { username: undefined, token: undefined };
    return session;
};

const deleteSession = () => {
    localStorage.removeItem("session");
};

//======================================================================================================================
// Local functions
//======================================================================================================================

const createHeaders = () => ({
    "FPET-Auth": `token ${getSession().token}`,
    "FPET-Language": i18next.language,
});

const createUrl = (path, query, appendSessionToken) => {
    let url = `${process.env.REACT_APP_WEB_SERVICE_URL}${path}`;

    if (query) {
        url = `${url}?${query}`;
    }

    if (appendSessionToken) {
        url = `${url}${!query ? "?" : "&"}token=${getSession().token}`;
    }

    return url;
};

const createDatabasesDownloadUrl = (databaseIds) => {
    return databaseIds && databaseIds.length
        ? createUrl("/api/v1/csv/databases/", databaseIds.map((databaseId) => `id=${databaseId}`).join("&"))
        : undefined;
};

const createRunLogDownloadUrl = (runId) => {
    const path = `/api/v1/logs/${runId}`;
    const query = undefined;
    return createUrl(path, query);
};

export const createResultsDownloadUrl = (
    runId,
    comparisonRunId,
    countryNumericCodeOrRegion1,
    countryNumericCodeOrRegion2,
    measures,
    indicators
) => {
    if (!countryNumericCodeOrRegion1 || !indicators || !indicators.length || !measures || !measures.length) {
        return;
    }

    const path = `/api/v1/csv/results/${runId}/${countryNumericCodeOrRegion1}/`;
    const query1 = comparisonRunId
        ? `comparisonRunId=${comparisonRunId}&comparisonRegion=${countryNumericCodeOrRegion2}&`
        : "";
    const query2 = measures
        .map((measure) => `measure=${measure}`)
        .concat(indicators.map((indicator) => `indicator=${indicator}`))
        .join("&");
    const query = query1 + query2;

    return createUrl(path, query);
};

export const createChartsDownloadUrl = (
    runId,
    comparisonRunId,
    countryNumericCodeOrRegion1,
    countryNumericCodeOrRegion2,
    maritalStatus,
    measure,
    indicators
) => {
    const path = `/api/v1/charts/${runId}/${countryNumericCodeOrRegion1}/${maritalStatus}/${measure}`;
    const query1 = comparisonRunId
        ? `comparisonRunId=${comparisonRunId}&comparisonRegion=${countryNumericCodeOrRegion2}&`
        : "";
    const query2 = indicators.map((indicator) => `indicator=${indicator}`).join("&");
    const query = query1 + query2;

    if (!(countryNumericCodeOrRegion1 && indicators && indicators.length)) {
        return undefined;
    }

    return createUrl(path, query);
};

export const createChartSetDownloadUrl = (
    runId,
    comparisonRunId,
    countryNumericCodeOrRegion1,
    countryNumericCodeOrRegion2,
    chartSet,
    maritalStatus,
    measure
) => {
    console.log('Measure in createChartSetDownloadUrl', measure);
    const path = `/api/v1/chartsets/${chartSet}/${runId}/${countryNumericCodeOrRegion1}/${maritalStatus}/${measure}`;
    const query = comparisonRunId
        ? `comparisonRunId=${comparisonRunId}&comparisonRegion=${countryNumericCodeOrRegion2}`
        : "";

    if (!countryNumericCodeOrRegion1) {
        return undefined;
    }

    return createUrl(path, query);
};

const fetch = (url, cancellationToken, config = {}) => {
    return webService
        .get(url, {
            headers: createHeaders(),
            cancelToken: cancellationToken,
            ...config,
        })
        .catch((error) => {
            if (axios.isCancel(error)) {
                console.log("Request canceled", error.message);
            }
            if (!axios.isCancel(error)) {
                console.log(error);
                throw error;
            }
        });
};

//======================================================================================================================
// webService
//======================================================================================================================

const webService = axios.create({
    baseURL: process.env.REACT_APP_WEB_SERVICE_URL,
});

webService.interceptors.response.use(
    (response) => {
        if (response) {
            if (response.config.url.includes("/session/")) {
                console.log("RESPONSE", response.config.method, "**LoginDetails**", response.data);
            } else {
                console.log("RESPONSE", response.config.method, response.config.url, response.data);
            }
        }
        return response;
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            if (!error.config.url.includes("/session/")) {
                deleteSession();
                window.location.href = "./";
            }
            return;
        }
        return Promise.reject(error);
    }
);

webService.interceptors.request.use(
    (config) => {
        if (config) {
            if (config.url.includes("/session/")) {
                console.log("REQUEST", config.method, "**LoginDetails**", config.data);
            } else {
                console.log("REQUEST", config.method, config.url, config.data);
            }
        }
        return config;
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            if (!error.config.url.includes("/session/")) {
                deleteSession();
                window.location.href = "./";
            }
            return;
        }
        return Promise.reject(error);
    }
);

if (developmentMode || window.location.search.includes("enable-api-mocking")) {
    new MockApiAdapter(webService);
}

//======================================================================================================================
// Helpers
//======================================================================================================================

export const getUsername = () => {
    return getSession().username;
};

export const getSignal = () => {
    return axios.CancelToken.source();
};

//======================================================================================================================
// API Calls
//======================================================================================================================

export const fetchSystemStatus = (cancellationToken) => {
    logAPI("fetchSystemStatus");
    return fetch("/api/v1/systeminfo", cancellationToken);
};

export const login = (username, password, cancellationToken) => {
    logAPI("login");
    return webService
        .post(`/api/v1/session/${username}?password=${password}`, {
            cancellationToken,
        })
        .then((response) => {
            createSession(username, response.data.token);
            return response;
        })
        .catch((error) => {
            if (!axios.isCancel(error)) {
                throw error;
            }
        });
};

export const logout = (username, cancellationToken) => {
    logAPI("logout");
    return webService
        .delete(`/api/v1/session/${username}`, {
            headers: createHeaders(),
            cancellationToken,
        })
        .then((response) => {
            deleteSession();
            return response;
        })
        .catch((error) => {
            if (!axios.isCancel(error)) {
                throw error;
            }
        });
};

export const fetchDatabases = (cancellationToken) => {
    logAPI("fetchDatabases");
    return fetch("/api/v1/databases/", cancellationToken);
};

export const uploadDatabase = (data, type, onProgress, cancellationToken) => {
    logAPI("uploadDatabase");
    return webService
        .post(`/api/v1/databases/files/${type}/`, data, {
            headers: createHeaders(),
            cancellationToken,
            onUploadProgress: onProgress,
        })
        .catch((error) => {
            if (!axios.isCancel(error)) {
                throw error;
            }
        });
};

export const startRun = ({
    name,
    surveyDatabaseId,
    populationDatabaseId,
    emuDatabaseId,
    countryNumericCode,
    regionCode,
    regionCodes,
    period,
    runType,
    cancellationToken,
}) => {
    logAPI("startRun");
    return webService.post(
        "/api/v1/runs/",
        {
            name: name,
            surveyDatabaseId: surveyDatabaseId,
            populationDatabaseId: populationDatabaseId,
            emuDatabaseId: emuDatabaseId,
            countryNumericCode: countryNumericCode,
            regionCode: regionCode,
            regionCodes: regionCodes,
            period: period,
            runType: runType,
        },
        {
            headers: createHeaders(),
            cancellationToken,
        }
    );
};

export const fetchRuns = (includePendingRuns, includeFailedRuns, cancellationToken) => {
    logAPI("fetchRuns");
    const path = "/api/v1/runs/";
    const query = `includePending=${includePendingRuns}&includeFailed=${includeFailedRuns}`;
    const url = createUrl(path, query);
    return fetch(url, cancellationToken);
};

export const fetchSurveyDatabase = (databaseId, divisionNumericCodeOrRegion, runId, runType, cancellationToken) => {
    logAPI("fetchSurveyDatabase");

    let url = `/api/v1/surveys/${databaseId}/${divisionNumericCodeOrRegion}`;
    if (runId) url += `?runId=${runId}`;
    if (runType) url += `?runType=${runType}`;

    return fetch(url, cancellationToken);
};

export const fetchPopulationDatabase = (databaseId, divisionNumericCodeOrRegion, runType, cancellationToken) => {
    logAPI("fetchPopulationDatabase");
    return fetch(`/api/v1/populations/${databaseId}/${divisionNumericCodeOrRegion}`, cancellationToken);
};

export const fetchEmuDatabase = (databaseId, divisionNumericCodeOrRegion, runType, cancellationToken) => {
    logAPI("fetchEmuDatabase");
    return fetch(`/api/v1/emu/${databaseId}/${divisionNumericCodeOrRegion}`, cancellationToken);
};

export const fetchResults = (
    runId,
    comparisonRunId,
    countryNumericCodeOrRegion1,
    countryNumericCodeOrRegion2,
    cancellationToken
) => {
    logAPI("fetchResults");
    const url = comparisonRunId
        ? `/api/v1/results/${runId}/${countryNumericCodeOrRegion1}?comparisonRunId=${comparisonRunId}&comparisonRegion=${countryNumericCodeOrRegion2}`
        : `/api/v1/results/${runId}/${countryNumericCodeOrRegion1}`;
    return fetch(url, cancellationToken);
};

export const downloadResults = (
    runId,
    comparisonRunId,
    countryNumericCodeOrRegion1,
    countryNumericCodeOrRegion2,
    measures,
    indicators,
    cancellationToken
) => {
    logAPI("downloadResults");
    const url = createResultsDownloadUrl(
        runId,
        comparisonRunId,
        countryNumericCodeOrRegion1,
        countryNumericCodeOrRegion2,
        measures,
        indicators
    );
    return fetch(url, cancellationToken);
};

export const downloadData = (runId, countryNumericCodeOrRegion, cancellationToken) => {
    logAPI("downloadData");
    const url = `/api/v1/csv/dataset/${runId}/${countryNumericCodeOrRegion}/`;
    return fetch(url, cancellationToken, { responseType: "blob" });
};

export const fetchChartSets = (cancellationToken) => {
    logAPI("fetchChartSets");
    return fetch("/api/v1/chartsets", cancellationToken);
};

export const downloadChartSet = (
    runId,
    comparisonRunId,
    countryNumericCodeOrRegion1,
    countryNumericCodeOrRegion2,
    chartSet,
    maritalStatus,
    measure,
    cancellationToken
) => {
    logAPI("downloadChartSet");
    const url = createChartSetDownloadUrl(
        runId,
        comparisonRunId,
        countryNumericCodeOrRegion1,
        countryNumericCodeOrRegion2,
        chartSet,
        maritalStatus,
        measure
    );

    return fetch(url, cancellationToken, { responseType: "arraybuffer" });
};

export const downloadCharts = (
    runId,
    comparisonRunId,
    countryNumericCodeOrRegion1,
    countryNumericCodeOrRegion2,
    maritalStatus,
    measure,
    indicators,
    cancellationToken
) => {
    logAPI("downloadCharts");

    const url = createChartsDownloadUrl(
        runId,
        comparisonRunId,
        countryNumericCodeOrRegion1,
        countryNumericCodeOrRegion2,
        maritalStatus,
        measure,
        indicators
    );

    return fetch(url, cancellationToken, { responseType: "arraybuffer" });
};

export const calculateTargets = (runId, countryNumericCodeOrRegion, parameters, cancellationToken) => {
    logAPI("calculateTargets");
    return webService
        .post(`/api/v1/targets/${runId}/${countryNumericCodeOrRegion}/`, parameters, {
            headers: createHeaders(),
            cancellationToken,
        })
        .catch((error) => {
            if (!axios.isCancel(error)) {
                throw error;
            }
        });
};

export const calculateProgress = (runId, countryNumericCodeOrRegion, parameters, cancellationToken) => {
    logAPI("calculateProgress");
    return webService
        .post(`/api/v1/progress/${runId}/${countryNumericCodeOrRegion}/`, parameters, {
            headers: createHeaders(),
            cancellationToken,
        })
        .catch((error) => {
            if (!axios.isCancel(error)) {
                throw error;
            }
        });
};

export const fetchRunLog = (runId, cancellationToken) => {
    logAPI("fetchRunLog");
    const url = createRunLogDownloadUrl(runId);
    return fetch(url, cancellationToken);
};

export const saveDatabase = (database, cancellationToken) => {
    logAPI("saveDatabase");
    return webService
        .post(`/api/v1/databases/documents/${database.type}`, database, {
            headers: createHeaders(),
            cancellationToken,
        })
        .catch((error) => {
            if (!axios.isCancel(error)) {
                throw error;
            }
        });
};

export const downloadDatabases = (databaseIds, cancellationToken) => {
    logAPI("downloadDatabases");
    const url = createDatabasesDownloadUrl(databaseIds);
    return fetch(url, cancellationToken, { responseType: "arraybuffer" });
};

export const deleteDatabases = (databaseIds, cancellationToken) => {
    logAPI("deleteDatabases");
    const query = databaseIds.map((databaseId) => `id=${databaseId}`).join("&");

    return webService
        .delete(`/api/v1/databases/?${query}`, {
            headers: createHeaders(),
            cancellationToken,
        })
        .catch((error) => {
            if (!axios.isCancel(error)) {
                throw error;
            }
        });
};

export const deleteRuns = (runIds, cancellationToken) => {
    logAPI("deleteRuns");
    const query = runIds.map((runId) => `id=${runId}`).join("&");

    return webService
        .delete(`/api/v1/runs/?${query}`, {
            headers: createHeaders(),
            cancellationToken,
        })
        .catch((error) => {
            if (!axios.isCancel(error)) {
                throw error;
            }
        });
};
